export type Plan = 'free' | 'stats' | 'reports';
export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'incomplete' | 'trialing';

export interface Parent {
  parentId: string;
  email: string;
  firstName?: string;
  displayName?: string;
  plan: Plan;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  subscriptionStatus?: SubscriptionStatus;
  currentPeriodEnd?: string;
  parentalConsentAt?: string;
  /** Counter maintained server-side; absent on legacy accounts pre-backfill. */
  kidsCount?: number;
  /** ISO timestamp set when the user dismisses the onboarding panel. */
  onboardedAt?: string;
  /** ISO timestamp set when the user finishes (or skips) the 3-screen welcome walkthrough. */
  welcomeSeenAt?: string;
  /** Opt-out toggle for the weekly AI digest email (paid plans only). */
  emailDigestOptOut?: boolean;
  /**
   * ISO timestamp set when a Stats/Reports owner dismisses the dashboard
   * family-share discovery card (Horizon 1 / H1.3). Absent ⇒ card still
   * shows. Owner-only; never set on co-parent records or free-tier owners.
   */
  familyShareSeenAt?: string;
  /**
   * ISO timestamp set when the parent dismisses the year-end "season recap
   * is ready" dashboard banner (Horizon 1 / H1.5).
   */
  seasonRecapBannerSeenAt?: string;
  /**
   * ISO timestamp set when the parent FIRST subscribed to the Reports
   * tier (set by the Stripe webhook on checkout.session.completed for
   * Reports). Used by the Upgrade page to decide whether to offer the
   * 7-day trial CTA — first-time Reports subscribers get it, returning
   * subscribers don't (Horizon 1 / H1.4).
   */
  hadReportsSubscriptionAt?: string;
  /**
   * Household role of the signed-in caller, populated by GET /api/v1/parent/me.
   *   - role 'owner'      → caller owns this Parent record; full access.
   *   - role 'co_parent'  → caller is invited; ownerEmail/callerEmail tell
   *     the UI who's who so it can render "you are a co-parent of …".
   */
  household?:
    | { role: 'owner' }
    | { role: 'co_parent'; ownerParentId: string; ownerEmail: string; callerEmail: string };
  createdAt: string;
}

export interface Kid {
  kidId: string;
  parentId: string;
  name: string;
  surname?: string;
  dob: string;
  position?: string;
  preferredFoot?: 'left' | 'right' | 'both';
  squadNumber?: number;
  kitColour?: string;
  clubName?: string;
  defaultTeamName?: string;
  seasonLabel?: string;
  notes?: string;
  /** S3 key for the profile photo. Use /kids/:id/photo-url to fetch a signed URL. */
  photoPath?: string;
  createdAt: string;
  updatedAt?: string;
  deletedAt?: string;
}

export interface CreateKidInput {
  name: string;
  surname?: string;
  dob: string;
  position?: string;
  preferredFoot?: 'left' | 'right' | 'both';
  squadNumber?: number;
  kitColour?: string;
  clubName?: string;
  defaultTeamName?: string;
  seasonLabel?: string;
  notes?: string;
}

export type UpdateKidInput = Partial<CreateKidInput> & { photoPath?: string };

export type MatchEventType = 'goal' | 'assist' | 'shot_on' | 'shot_off' | 'save' | 'defensive';

export interface MatchEvent {
  type: MatchEventType;
  minute: number;
  half: 1 | 2 | 'et';
  note?: string;
  /**
   * Optional pitch location of the event, in normalised pitch coordinates.
   *   x: 0 = left touchline,  100 = right touchline
   *   y: 0 = own goal end,    100 = opposition goal end
   * Both are present together or neither — server validates the pair.
   * Heat-maps render only events with {x, y}.
   */
  x?: number;
  y?: number;
}

export type MatchType = 'league' | 'cup' | 'friendly' | 'tournament' | 'training';

export const MATCH_TYPES: readonly MatchType[] = [
  'league',
  'cup',
  'friendly',
  'tournament',
  'training',
] as const;

export type MatchFormat = '3v3' | '5v5' | '7v7' | '9v9' | '11v11';

export const MATCH_FORMATS: readonly MatchFormat[] = ['3v3', '5v5', '7v7', '9v9', '11v11'] as const;

export interface Substitution {
  minute: number;
  direction: 'on' | 'off';
}

export interface Match {
  parentId: string;
  kidId: string;
  matchId: string;
  date: string;
  kickoff?: string;
  opposition?: string;
  homeAway?: 'home' | 'away';
  matchType: MatchType;
  matchFormat?: MatchFormat;
  matchTitle?: string;
  matchLengthMin: number;
  ourScore?: number;
  theirScore?: number;
  /** @deprecated use `positions[]` */
  position?: string;
  positions?: string[];
  positionTimeline?: Array<{ minute: number; position: string }>;
  kidRating?: number;
  manOfMatch?: boolean;
  captain?: boolean;
  /** @deprecated use `substitutions[]` */
  subbedOnAt?: number;
  /** @deprecated use `substitutions[]` */
  subbedOffAt?: number;
  substitutions?: Substitution[];
  note?: string;
  events: MatchEvent[];
  minutesPlayed?: number;
  /** S3 key of the recording — only present while transcription is pending; deleted after. */
  voiceNotePath?: string;
  voiceTranscript?: string;
  voiceTranscriptStatus?: 'pending' | 'done' | 'failed';
  voiceTranscriptJob?: string;
  voiceNoteDurationSec?: number;
  /** LLM-generated magazine cover line (Reports tier only). */
  aiCoverLine?: string;
  /** Parent-supplied cover line. Beats `aiCoverLine` and deterministic
   *  fallback. Setting this also suppresses future AI regen for the
   *  match. Length cap: 60 chars (enforced server-side). */
  coverLineOverride?: string;
  /** LLM-generated match report (Reports tier only). Cached, regenerable. */
  aiReport?: { text: string; generatedAt: string; modelId: string };
  /** @deprecated legacy single-photo field; reads should prefer imagePaths */
  imagePath?: string;
  /** Unified list of S3 keys for this match's photos (plan-capped). */
  imagePaths?: string[];
  imageUrl?: string;  // server-side computed signed GET URL when imagePath is set
  createdAt: string;
  updatedAt?: string;
  deletedAt?: string;
}

export interface UpdateMatchInput {
  date?: string;
  kickoff?: string;
  opposition?: string;
  homeAway?: 'home' | 'away';
  matchType?: MatchType;
  matchFormat?: MatchFormat;
  matchTitle?: string;
  matchLengthMin?: number;
  ourScore?: number;
  theirScore?: number;
  /** @deprecated use `positions[]` */
  position?: string;
  positions?: string[];
  positionTimeline?: Array<{ minute: number; position: string }>;
  kidRating?: number;
  manOfMatch?: boolean;
  captain?: boolean;
  substitutions?: Substitution[];
  note?: string;
  events?: MatchEvent[];
  minutesPlayed?: number;
  /** Parent's hand-written cover line — empty string OR null clears. */
  coverLineOverride?: string | null;
}

// ── Achievements ─────────────────────────────────────────────────────────

export type AchievementCategory =
  | 'firsts'
  | 'milestones'
  | 'feats'
  | 'streaks'
  | 'variety'
  | 'records';

export type AchievementRarity = 'common' | 'rare' | 'epic' | 'legendary';

export interface Achievement {
  id: string;
  title: string;
  description: string;
  category: AchievementCategory;
  rarity: AchievementRarity;
  unlockedAt?: string;
  unlockedMatchId?: string;
}

export interface AchievementProgress {
  id: string;
  title: string;
  description: string;
  category: AchievementCategory;
  rarity: AchievementRarity;
  current: number;
  target: number;
}

export interface AchievementsResponse {
  unlocked: Achievement[];
  inProgress: AchievementProgress[];
  locked: AchievementProgress[];
}

// ── Stats ────────────────────────────────────────────────────────────────

export interface StatsAggregate {
  matches: number;
  minutesPlayed: number;
  goals: number;
  assists: number;
  shotsOn: number;
  shotsOff: number;
  saves: number;
  defensive: number;
  wins: number;
  draws: number;
  losses: number;
  cleanSheets: number;
  momCount: number;
  captainCount: number;
  averageRating: number | null;
  biggestWin: { ourScore: number; theirScore: number; opposition?: string; matchId: string } | null;
  topOpposition: { opposition: string; goals: number } | null;
}

export interface SeasonStats extends StatsAggregate {
  seasonLabel: string;
}

export interface CareerStats {
  career: StatsAggregate;
  seasons: SeasonStats[];
}

// ── Plan limits ──────────────────────────────────────────────────────────

export interface PlanLimits {
  plan: Plan;
  kidsUsed: number;
  kidsLimit: number;
  matchesUsedThisYear: number;
  matchesLimit: number;
  eventsPerMatchLimit: number;
  noteCharsLimit: number;
  imagesPerMatchLimit: number;
}
